import { db } from '../services/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Test function to verify Firestore write permissions for shared_passwords collection
 * @param {string} userId - The user ID to test with
 * @returns {Promise<boolean>} - Returns true if write was successful, false otherwise
 */
export async function testFirestoreWrite(userId) {
  if (!userId) {
    console.error('testFirestoreWrite: No userId provided');
    return false;
  }

  try {
    console.log('Testing Firestore write for userId:', userId);
    
    // Create a simple test document with minimal data
    const testDocId = `test_${Date.now()}`;
    const testDocRef = doc(db, 'users', userId, 'sharedPasswords', testDocId);
    
    const testData = {
      test: true,
      message: 'This is a test document',
      timestamp: serverTimestamp(),
      userId: userId
    };

    console.log('Attempting to write test document:', {
      path: `users/${userId}/sharedPasswords/${testDocId}`,
      data: testData
    });

    // Try to create the document
    await setDoc(testDocRef, testData);
    
    console.log('✅ Test document created successfully!');
    console.log('Document path:', `users/${userId}/sharedPasswords/${testDocId}`);
    
    return true;
  } catch (error) {
    console.error('❌ Failed to create test document:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    
    // Log more detailed error information
    if (error.code === 'permission-denied') {
      console.error('Permission denied - check Firestore security rules');
    } else if (error.code === 'unauthenticated') {
      console.error('User is not authenticated');
    }
    
    return false;
  }
}

/**
 * Test function with even simpler data structure
 * @param {string} userId - The user ID to test with
 * @returns {Promise<boolean>} - Returns true if write was successful, false otherwise
 */
export async function testMinimalFirestoreWrite(userId) {
  if (!userId) {
    console.error('testMinimalFirestoreWrite: No userId provided');
    return false;
  }

  try {
    console.log('Testing minimal Firestore write for userId:', userId);
    
    const testDocId = `minimal_test_${Date.now()}`;
    const testDocRef = doc(db, 'users', userId, 'sharedPasswords', testDocId);
    
    // Absolute minimal data
    const minimalData = {
      created: Date.now()
    };

    console.log('Attempting minimal write:', {
      path: `users/${userId}/sharedPasswords/${testDocId}`,
      data: minimalData
    });

    await setDoc(testDocRef, minimalData);
    
    console.log('✅ Minimal test document created successfully!');
    
    return true;
  } catch (error) {
    console.error('❌ Failed to create minimal test document:', error);
    console.error('Full error object:', JSON.stringify(error, null, 2));
    
    return false;
  }
}